import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { PointoramaPlane } from './PointoramaPlane';
import { DefaultColors } from '../defines';

/** Minimal plane interface: a normal + a position. Satisfied by both
 *  PointoramaPlane and PointoramaSurface (the latter replaced plane annotations). */
type PlaneLike = { getNormal(): THREE.Vector3; position: THREE.Vector3 };

export class FloorplanLine extends Line2 {
  firstPointOnLine: THREE.Vector3 | undefined = undefined;
  secondPointOnLine: THREE.Vector3 | undefined = undefined;
  startingCornerPoint: THREE.Vector3 | undefined = undefined;
  endingCornerPoint: THREE.Vector3 | undefined = undefined;
  direction: THREE.Vector3 = new THREE.Vector3();
  constructor() {
    super();
    this.material = this.getLineMaterial();
  }

  reset() {
    this.firstPointOnLine = undefined;
    this.secondPointOnLine = undefined;
    this.startingCornerPoint = undefined;
    this.endingCornerPoint = undefined;
  }

  getLineMaterial() {
    return new LineMaterial({
      color: DefaultColors.OBJECTS,
      linewidth: 3,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      depthTest: false,
    });
  }

  defineFromIntersectingPlanes(plane1: PlaneLike, plane2: PlaneLike) {
    const normal1 = plane1.getNormal();
    const normal2 = plane2.getNormal();
    this.direction = normal1.clone().cross(normal2).normalize();

    const a1 = normal1.x,
      b1 = normal1.y,
      c1 = normal1.z,
      d1 = normal1.dot(plane1.position);
    const a2 = normal2.x,
      b2 = normal2.y,
      c2 = normal2.z,
      d2 = normal2.dot(plane2.position);

    let x = 0,
      y = 0,
      z = 0;
    if (Math.abs(this.direction.x) > 0.7) {
      const denominator = c2 * b1 - c1 * b2;
      if (!denominator) {
        this.firstPointOnLine = undefined;
        this.secondPointOnLine = undefined;
        return;
      }

      x = plane2.position.x;
      y = (c2 * (d1 - a1 * x) - c1 * (d2 - a2 * x)) / denominator;
      z = -(b2 * (d1 - a1 * x) - b1 * (d2 - a2 * x)) / denominator;
    } else if (Math.abs(this.direction.y) > 0.7) {
      const denominator = a2 * c1 - a1 * c2;
      if (!denominator) {
        this.firstPointOnLine = undefined;
        this.secondPointOnLine = undefined;
        return;
      }

      y = plane2.position.y;
      z = (a2 * (d1 - b1 * y) - a1 * (d2 - b2 * y)) / denominator;
      x = -(c2 * (d1 - b1 * y) - c1 * (d2 - b2 * y)) / denominator;
    } else {
      const denominator = b2 * a1 - b1 * a2;
      if (!denominator) {
        this.firstPointOnLine = undefined;
        this.secondPointOnLine = undefined;
        return;
      }

      z = plane2.position.z;
      x = (b2 * (d1 - c1 * z) - b1 * (d2 - c2 * z)) / denominator;
      y = -(a2 * (d1 - c1 * z) - a1 * (d2 - c2 * z)) / denominator;
    }

    const linePoint = new THREE.Vector3(x, y, z);

    // Plane positions projected on the section line
    this.firstPointOnLine = this.direction.clone().multiplyScalar(plane1.position.clone().sub(linePoint).dot(this.direction)).add(linePoint);
    this.secondPointOnLine = this.direction.clone().multiplyScalar(plane2.position.clone().sub(linePoint).dot(this.direction)).add(linePoint);
  }

  defineFromIntersectingExistingPlanes(plane1: PointoramaPlane, plane2: PointoramaPlane, line: FloorplanLine | null) {
    this.defineFromIntersectingPlanes(plane1, plane2);

    if (!line) return;

    this.secondPointOnLine = this.getStart().clone().add(line.getDirection());
  }

  findPointOfIntersectionWithOtherLine(otherLine: FloorplanLine) {
    if (!this.firstPointOnLine || !otherLine.firstPointOnLine) return;
    const P1 = this.firstPointOnLine;
    const P2 = otherLine.firstPointOnLine;
    const v1 = this.direction;
    const v2 = otherLine.direction;
    const P1_P2 = new THREE.Vector3().subVectors(P1, P2);

    const v1_dot_v1 = v1.dot(v1);
    const v2_dot_v2 = v2.dot(v2);
    const v1_dot_v2 = v1.dot(v2);
    const P1_P2_dot_v1 = P1_P2.dot(v1);
    const P1_P2_dot_v2 = P1_P2.dot(v2);

    const denominator = v1_dot_v1 * v2_dot_v2 - v1_dot_v2 * v1_dot_v2;

    if (denominator === 0) {
      return;
    }

    const t1 = (P1_P2_dot_v2 * v1_dot_v2 - P1_P2_dot_v1 * v2_dot_v2) / denominator;
    const t2 = (P1_P2_dot_v2 * v1_dot_v1 - P1_P2_dot_v1 * v1_dot_v2) / denominator;

    const closestPointOnLine1 = P1.clone().addScaledVector(v1, t1);
    const closestPointOnLine2 = P2.clone().addScaledVector(v2, t2);

    const difference = Math.abs(closestPointOnLine1.x - closestPointOnLine2.x);
    if (difference > 0.001) {
      console.error('Two floorplanlines are not in the same plane. Difference: ', difference);
    } else if (difference > 0.000001) {
      console.warn('Two floorplanlines are not in the same plane. Difference: ', difference);
    }

    if (Number.isNaN(closestPointOnLine1.x) || Number.isNaN(closestPointOnLine1.y) || Number.isNaN(closestPointOnLine1.z)) {
      console.warn('Intersection point between two floorplanlines contains NaN values!');
      return;
    }
    return closestPointOnLine1;
  }

  setFirstCornerPoint(cornerPoint: THREE.Vector3) {
    this.startingCornerPoint = cornerPoint;
  }

  setSecondCornerPoint(cornerPoint: THREE.Vector3) {
    this.endingCornerPoint = cornerPoint;
  }

  getStart() {
    if (this.startingCornerPoint) {
      return this.startingCornerPoint;
    } else if (this.firstPointOnLine) {
      return this.firstPointOnLine;
    } else {
      return new THREE.Vector3();
    }
  }

  getEnd() {
    if (this.endingCornerPoint) {
      return this.endingCornerPoint;
    } else if (this.secondPointOnLine) {
      return this.secondPointOnLine;
    } else {
      return new THREE.Vector3();
    }
  }

  updateGeometry() {
    const start = this.getStart();
    const end = this.getEnd();
    const lineVertices = [start.x, start.y, start.z, end.x, end.y, end.z];
    this.geometry.dispose();
    this.geometry = new LineGeometry().setPositions(lineVertices);
  }

  isPointOnLine(point: THREE.Vector3) {
    const start = this.getStart();
    const end = this.getEnd();
    const distance = point.clone().sub(end).cross(this.direction.clone().normalize()).length();
    const length = start.distanceTo(end);
    return distance < 0.1 * length && point.distanceTo(end) + point.distanceTo(start) < 1.1 * length;
  }

  isLineAlmostParallel(direction: THREE.Vector3) {
    return this.direction.clone().normalize().cross(direction.clone().normalize()).length() < 0.03;
  }

  getLength() {
    return this.getStart().clone().sub(this.getEnd()).length();
  }

  getDirection() {
    return this.getEnd().clone().sub(this.getStart()).normalize();
  }
}
